import styles from '../../MaskOverlay.module.scss';

import { Delete } from '@mui/icons-material';
import { Typography } from '@mui/material';
import { cloneDeep, isEqual, merge } from 'lodash';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useHistoryEvents } from '@work4all/components/lib/components/entity-preview/EntityHistory/use-history-events';
import { Tooltip } from '@work4all/components/lib/components/tooltip/Tooltip';
import { useLock } from '@work4all/components/lib/hooks';

import { useDataProvider, useFormPlus, useUser } from '@work4all/data';
import { useCustomFieldsConfig } from '@work4all/data/lib/custom-fields';
import {
  TempFileManagerContext,
  useTempFileManager,
} from '@work4all/data/lib/hooks/data-provider/useTempFileManager';
import { useEntityJsonSchema } from '@work4all/data/lib/json-schema/EntityJsonSchemasContext';

import { Customer } from '@work4all/models/lib/Classes/Customer.entity';
import { InputErpAnhangAttachementsRelation } from '@work4all/models/lib/Classes/InputErpAnhangAttachementsRelation.entity';
import { Supplier } from '@work4all/models/lib/Classes/Supplier.entity';
import { DataRequest } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { useJSONSchemaResolver } from '@work4all/utils';
import {
  canAddServiceContracts,
  canDeleteServiceContracts,
  canViewServiceContracts,
} from '@work4all/utils/lib/permissions';

import useAttachementsRelation from '../../../../../hooks/useAttachementsRelation';
import { useBinaryTransfer } from '../../../../../hooks/useBinaryTransfer';
import { MenuItem } from '../../../locked-inputs';
import {
  MaskTab,
  MaskTabContext,
  MaskTabPanel,
  MaskTabs,
} from '../../../mask-tabs';
import { Form, MaskOverlayHeader } from '../../components';
import { INDIVIDUAL_TAB_ID } from '../../components/custom-fields/contants';
import { IndividualTabPanel } from '../../components/custom-fields/IndividualTabPanel';
import {
  normalizeCustomFields,
  NormalizedData,
} from '../../components/custom-fields/normalize-custom-fields';
import { prepareInputWithCustomFields } from '../../components/custom-fields/prepare-input-with-custom-fields';
import { LockOverride } from '../../components/LockOverride';
import { MaskContent } from '../../components/MaskContent/MaskContent';
import { MaskOverlayFullscreenToggleButton } from '../../components/MaskOverlayFullscreenToggleButton';
import { MaskOverlayMenuWrapper } from '../../components/MaskOverlayMenuWrapper';
import { MaskOverlaySubmitButton } from '../../components/MaskOverlaySubmitButton';
import { HistoryTabPanel } from '../../components/tab-panels/history/HistoryTabPanel';
import {
  MaskContextProvider,
  useMaskConfig,
  useMaskContextValue,
} from '../../hooks/mask-context';
import { useConfirmBeforeCloseMask } from '../../hooks/use-confrm-before-close-mask';
import { EntityRightsContext } from '../../hooks/use-entity-rights';
import {
  MaskLockContextProvider,
  useMaskLock,
} from '../../hooks/use-mask-lock';
import { useStandardDeleteEntityHandler } from '../../hooks/use-standard-delete-entity-handler';
import { normalizeFormValue } from '../../hooks/useExtendedFormContext';
import { MaskControllerProps } from '../../types';
import { pickUpdateFields } from '../../utils/pick-update-fields';
import { parseTemplate } from '../../utils/use-assignable-template-entity';
import {
  ErpInitialView,
  useErpInitialView,
} from '../erp/components/initial-view/ErpInitialView';

import { AttachmentsTabPanel } from './components/tab-panels/attachments/AttachmentsTabPanel';
import { ComponentsTabPanel } from './components/tab-panels/components/ComponentsTabPanel';
import { GeneralTabPanel } from './components/tab-panels/general/GeneralTabPanel';
import { useServiceContractFormUpdate } from './hooks/use-service-contract-form-update';
import { useServiceContractRequestData } from './hooks/use-service-contract-request-data';
import { useShadowServiceContractObjectApi } from './hooks/use-shadow-service-contract-object-api';
import { ShadowServiceContractObjectApiContextProvider } from './hooks/use-shadow-service-contract-object-api-context';
import { ServiceContractsFormValue, ServiceContractsRemoteData } from './types';
import { useLockedServiceContractCustomers } from './use-locked-service-contract-customers';

export const ServiceContractOverlayControllerInternal = (
  props: MaskControllerProps
) => {
  const { t } = useTranslation();
  const { onAfterSave } = props;

  const user = useUser();
  const mask = useMaskConfig(props);

  const lock = useMaskLock();

  const shouldUseRegularQuery = !lock.isLoading && lock.isLocked;
  const shouldUseshadowServiceContractObjectApi =
    !lock.isLoading && !lock.isLocked;

  const schema = useEntityJsonSchema(mask.entity);

  const customRules = useCallback(
    (input: ServiceContractsRemoteData) => {
      const errors: Record<string, { message: string; type: string }> = {};

      if (!input.mainAddressSdObjMemberId || !input.mainAddress) {
        errors.mainAddress = {
          message: t('ERROR.FIELD_REQUIRED'),
          type: 'customValidation',
        };
      }

      if (!input.name || input.name.trim() === '') {
        errors.name = {
          message: t('ERROR.FIELD_REQUIRED'),
          type: 'customValidation',
        };
      }

      return Object.keys(errors).length > 0 ? errors : true;
    },
    [t]
  );

  const resolver = useJSONSchemaResolver(schema, customRules);

  const template = parseTemplate(mask);

  const initialProps = useErpInitialView(
    !template && !mask.id,
    props.onAfterSave
  );

  const { data: businessPartnerData } = useDataProvider(
    useMemo<DataRequest>(() => {
      return {
        data: {
          id: null,
          name: null,
          number: null,
          mainContact: {
            id: null,
            displayName: null,
          },
        } as Customer | Supplier,
        entity: template?.entity,
        filter: [{ id: { $eq: template?.businessPartnerId } }],
      };
    }, [template?.businessPartnerId, template?.entity]),
    !template
  );

  const customFields = useCustomFieldsConfig({ entity: mask.entity });

  const request: DataRequest = useServiceContractRequestData(mask.id);

  const query = useDataProvider(
    useMemo(() => {
      const data = request.data;

      return {
        skip: !shouldUseRegularQuery,
        filter: [{ id: { $eq: mask.id } }],
        entity: mask.entity,
        data,
      };
    }, [mask.entity, mask.id, request, shouldUseRegularQuery])
  );

  const skipShadowApi =
    !shouldUseshadowServiceContractObjectApi ||
    (mask.isCreateMode &&
      !initialProps.businessPartner &&
      !businessPartnerData?.[0]);

  const [shadowObject, shadowServiceContractObjectApi] =
    useShadowServiceContractObjectApi({
      entity: mask.entity,
      id: mask.id,
      parentEntity: Entities.customer,
      parentId:
        businessPartnerData?.[0]?.id || initialProps.businessPartner?.id,
      contactId:
        businessPartnerData?.[0]?.mainContact?.id ||
        initialProps.businessPartner?.mainContact?.id,
      skip: skipShadowApi,
    });

  const data =
    (shouldUseRegularQuery
      ? query.data[0]
      : shouldUseshadowServiceContractObjectApi
      ? shadowObject?.data
      : null) ?? null;

  const loading = shouldUseRegularQuery
    ? query.loading
    : shouldUseshadowServiceContractObjectApi
    ? shadowServiceContractObjectApi.loading
    : false;

  const maskContext = useMaskContextValue({ ...mask, data, customFields });

  const customerLocked = useLockedServiceContractCustomers(user.kundennummer);
  const form = useFormPlus<ServiceContractsFormValue>({
    resolver,
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: data,
    context: {
      schema,
    },
  });
  const { formState, handleSubmit } = form;

  // Keep track of fields that ever changed in the form since last reset. This
  // is required to determine if a field was changed even if the value is the
  // same as the initial one.
  const persistedDirtyFieldsRef = useRef<
    (typeof form)['formState']['dirtyFields']
  >({});

  useEffect(() => {
    // Resetting values is an expensive operation. If the value hasn't changed,
    // we can skip it to improve responsiveness. We still need to reset to
    // update dirty fields.

    // Compare form values with data, excluding the 'id' field to prevent
    // unnecessary form resets when data gains an 'id' after quick save
    const { id: _formId, ...formValues } = form.getValues() ?? {};
    const { id: _dataId, ...dataWithoutId } = data ?? {};
    const normalizedData = normalizeCustomFields(
      normalizeFormValue(dataWithoutId),
      customFields
    );

    const shouldKeepValues = isEqual(formValues, normalizedData);

    form.reset(normalizedData as Partial<ServiceContractsFormValue>, {
      keepValues: shouldKeepValues,
      keepErrors: true,
    });

    // Every time the form is reset we reset the dirty fields.
    persistedDirtyFieldsRef.current = {};
  }, [form, data, customFields]);

  useServiceContractFormUpdate(form);

  const isDirty = shadowServiceContractObjectApi.isDirty;

  const lastFormValues = useRef<ServiceContractsFormValue>(null);

  useEffect(() => {
    const subscription = form.watch((formValues, info) => {
      // Updates caused by calling `form.reset` will have an empty `name`
      // property. These should be skipped.
      if (!info.name) return;

      const formDirtyFields = form.formState.dirtyFields;
      const persistentDirtyFields = persistedDirtyFieldsRef.current;

      // Send all fields that were changed since last reset.
      const dirtyFields = merge(persistentDirtyFields, formDirtyFields);

      const isValueEqual = isEqual(formValues, lastFormValues.current);
      const isMainAddressSelected = !!formValues.mainAddress;
      const isFormDirty = Object.keys(dirtyFields).length > 0;

      if (isValueEqual || !isMainAddressSelected || !isFormDirty) {
        return;
      }

      const { __typename, ...input } = formValues;
      if (!input.terminationDate) input.terminationDate = null;
      if (!input.nextInvoiceDate) input.nextInvoiceDate = null;
      if (!input.serviceContractStartDate)
        input.serviceContractStartDate = null;
      if (!input.serviceContractEndDate) input.serviceContractEndDate = null;

      const toModify = pickUpdateFields(input, dirtyFields);
      const toModifyPrepared = prepareInputWithCustomFields(
        toModify as NormalizedData<ServiceContractsFormValue>,
        customFields
      );

      lastFormValues.current = cloneDeep(formValues);
      persistedDirtyFieldsRef.current = dirtyFields;

      shadowServiceContractObjectApi.modify(toModifyPrepared);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [
    form,
    shadowServiceContractObjectApi,
    shadowServiceContractObjectApi.modify,
    customFields,
  ]);

  const entityRights = useMemo(
    () => ({
      create: canAddServiceContracts(user),
      read: canViewServiceContracts(user),
      update: canAddServiceContracts(user),
      delete: canDeleteServiceContracts(user),
    }),
    [user]
  );

  const currentAttachements = useMemo(
    () =>
      data?.attachements?.map((x) => ({
        ...x,
        fileName: x?.fileInfos?.fileEntityFilename,
        fileSize: x?.fileInfos?.fileSize,
      })),
    [data?.attachements]
  );

  const tempFileManager = useTempFileManager(currentAttachements);

  const attachments =
    useAttachementsRelation<InputErpAnhangAttachementsRelation>(
      tempFileManager,
      Entities.erpAttachment,
      'id'
    );

  const attachementsDirty = attachments?.attachements
    ? Object.entries(attachments?.attachements)
        .filter((x) => x[1])
        .some((x) => x[1].length)
    : false;

  // Save comments which are left in Text Editor, but not saved
  const { comment, addComment } = useHistoryEvents(mask.entity, mask.id);
  const commentDirty = Boolean(comment);

  const onSubmit = useCallback(async () => {
    await addComment(comment);
    // Wait for all updates to be done before persist
    await new Promise((resolve) => setTimeout(resolve, 0));
    const submitShadowObject = async () => {
      const succeeded = await shadowServiceContractObjectApi.persist(
        attachments?.attachements
      );
      if (succeeded) onAfterSave?.(null);
    };

    await submitShadowObject();
  }, [
    addComment,
    comment,
    shadowServiceContractObjectApi,
    attachments?.attachements,
    onAfterSave,
  ]);

  const normalizedData = useMemo(() => {
    return normalizeCustomFields(normalizeFormValue(data), customFields);
  }, [data, customFields]);

  const shadowServiceContractObjectApiWithAttachements = useMemo(() => {
    const computedIsDirty = attachments?.isDirty || isDirty || commentDirty;
    return {
      ...shadowServiceContractObjectApi,
      isDirty: computedIsDirty,
      persist: async () =>
        await shadowServiceContractObjectApi.persist(attachments?.attachements),
      positions: normalizedData.positionList,
    };
  }, [
    attachments?.isDirty,
    attachments?.attachements,
    isDirty,
    shadowServiceContractObjectApi,
    normalizedData.positionList,
    commentDirty,
  ]);

  const computedIsDirty = attachementsDirty || isDirty || formState.isDirty;
  const isFormValid = formState.isValid;
  useConfirmBeforeCloseMask(computedIsDirty);

  const canDelete = !mask.wip && mask.isEditMode && entityRights.delete;

  const bt = useBinaryTransfer();

  const handleDeleteEntitiesClick = useStandardDeleteEntityHandler({
    ...mask,
    entity: Entities.serviceContract,
  });

  useEffect(() => {
    if (mask.params?.basedon === 'transfer' && bt.transferData) {
      const files = bt.transferData;

      if (!(files?.length > 0)) {
        return;
      }

      tempFileManager.uploadFiles(files);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { locked } = useLock();

  const formDisabled = Boolean(loading || locked || customerLocked);

  const shouldRenderIndividualTab = customFields && customFields.length > 0;

  const subTitle = mask.isCreateMode
    ? data?.mainAddress?.businessPartner?.data?.name || ''
    : `${[data?.number, data?.name].filter(Boolean).join(' | ')}`;

  return (
    <LockOverride
      forceLock={formDisabled}
      lockReason={
        customerLocked ? t('ALERTS.SERVICE_CONTRACT.MASK_DISABLED') : undefined
      }
    >
      <MaskContextProvider value={maskContext}>
        <EntityRightsContext.Provider value={entityRights}>
          <TempFileManagerContext.Provider value={tempFileManager}>
            <ShadowServiceContractObjectApiContextProvider
              value={shadowServiceContractObjectApiWithAttachements}
            >
              <FormProvider {...form}>
                <MaskTabContext
                  defaultValue={mask.params?.tab || props.openTab || 'general'}
                >
                  <Form
                    onSubmit={handleSubmit(onSubmit)}
                    className={styles.maskForm}
                  >
                    <MaskOverlayHeader
                      title={t(`COMMON.SERVICECONTRACT`)}
                      subTitle={subTitle}
                      actions={
                        <>
                          <MaskOverlaySubmitButton
                            loading={formState.isSubmitting}
                            disabled={!computedIsDirty || !isFormValid}
                          />
                          <MaskOverlayFullscreenToggleButton />
                          <MaskOverlayMenuWrapper>
                            <Tooltip
                              title={
                                canDelete || mask.isCreateMode
                                  ? undefined
                                  : t('RIGHTS.MISSING')
                              }
                              activateForDisabled={true}
                            >
                              <MenuItem
                                disabled={!canDelete}
                                onClick={handleDeleteEntitiesClick}
                              >
                                <Delete />
                                <Typography marginLeft="0.5rem">
                                  {t('COMMON.DELETE')}
                                </Typography>
                              </MenuItem>
                            </Tooltip>
                          </MaskOverlayMenuWrapper>
                        </>
                      }
                      tabs={
                        <ServiceContractTabs
                          isCreateMode={mask.isCreateMode}
                          renderIndividualTab={shouldRenderIndividualTab}
                        />
                      }
                    />
                    <Content renderIndividualTab={shouldRenderIndividualTab} />
                    <ErpInitialView
                      {...initialProps}
                      entity={Entities.customer}
                      loading={loading}
                    />
                  </Form>
                </MaskTabContext>
              </FormProvider>
            </ShadowServiceContractObjectApiContextProvider>
          </TempFileManagerContext.Provider>
        </EntityRightsContext.Provider>
      </MaskContextProvider>
    </LockOverride>
  );
};

const ServiceContractTabs = memo(function ServiceContractTabs({
  isCreateMode,
  renderIndividualTab,
}: {
  isCreateMode: boolean;
  renderIndividualTab: boolean;
}) {
  const { t } = useTranslation();

  return (
    <MaskTabs>
      <MaskTab value="general" label={t('MASK.GENERAL')}></MaskTab>
      <MaskTab value="components" label={t('MASK.COMPONENT_other')}></MaskTab>
      {renderIndividualTab && (
        <MaskTab value={INDIVIDUAL_TAB_ID} label={t('MASK.INDIVIDUAL')} />
      )}
      <MaskTab value="attachments" label={t('MASK.ATTACHMENTS')}></MaskTab>
      <MaskTab
        value="history"
        label={t('MASK.HISTORY')}
        disabled={isCreateMode}
      ></MaskTab>
    </MaskTabs>
  );
});

const Content = memo(function AppointmentTabPanels({
  renderIndividualTab,
}: {
  renderIndividualTab: boolean;
}) {
  return (
    <MaskContent className={styles.root}>
      <MaskTabPanel value="general">
        <GeneralTabPanel />
      </MaskTabPanel>

      <MaskTabPanel value="components">
        <ComponentsTabPanel />
      </MaskTabPanel>

      {renderIndividualTab && (
        <MaskTabPanel value={INDIVIDUAL_TAB_ID}>
          <IndividualTabPanel />
        </MaskTabPanel>
      )}

      <MaskTabPanel value="attachments">
        <AttachmentsTabPanel />
      </MaskTabPanel>

      <MaskTabPanel value="history">
        <HistoryTabPanel />
      </MaskTabPanel>
    </MaskContent>
  );
});

export function ServiceContractOverlayController(props: MaskControllerProps) {
  const mask = useMaskConfig(props);

  return (
    <MaskLockContextProvider entity={mask.entity} id={mask.id}>
      <ServiceContractOverlayControllerInternal {...props} />
    </MaskLockContextProvider>
  );
}
